import { requester } from "App";
import { mobileNoFormat } from "InitialData/regExp";
import InputText from "components/inputs/signUp/InputText";
import Container from "layouts/container/Container";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import biometricService from "shared/BiometricService";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { checkFirstTimeUser, getPhoneNumberPayload, getTokenPayload, validateInputs } from "shared/utils/Utils";
import styles from "./MobileNumberSignIn.module.css";
import { devLog } from "utils/logger";
import { AuthContext } from "providers/AuthProvider";

function MobileNumberSignIn(props) {
    const inputs = useRef([]);
    const { userId, userName } = useContext(AuthContext)
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [termsChecked, setTermsChecked] = useState(false);
    const [privacyChecked, setPrivacyChecked] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [authMethod, setAuthMethod] = useState(null);
    const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
    const [biometricStatus, setBiometricStatus] = useState({
        supported: false,
        platform: 'unknown',
        loading: false,
        error: null
    });

    const { token } = useSelector((state) => ({
        token: state.auth.token,
    }));

    const [searchParams] = useSearchParams();

    // 파라미터 안전하게 추출
    const urlParams = {
        deviceCode: searchParams.get('deviceCode'),
        storeCode: searchParams.get('storeCode'),
        type: searchParams.get('type')
    };

    const isCardAuthMode = urlParams.type === 'card_payment_auth';
    const currentStoreCode = urlParams.storeCode || props.storeCode;

    // isOurAppWebView 함수 추가
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        // 웹뷰 감지 로직
        const isWebView = (() => {
            const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
            const isAndroidWebView = /wv/.test(userAgent) || /android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);
            return isIOSWebView || isAndroidWebView;
        })();
        // 웹뷰 확인
        if (isWebView) {
            if (window.BRIDGE && window.BRIDGE.isOurAppWebView && window.BRIDGE.isOurAppWebView()) {
                return true;
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                return true;
            }
        }
        return false;
    };

    // ==================== 모든 useEffect는 여기에 배치 ====================

    // 카드 인증 모드 체크 및 리다이렉트
    useEffect(() => {
        if (isOurAppWebView() && isCardAuthMode) {
            // CardAuthVerification으로 즉시 이동
            navigate("/card-auth-verification", {
                state: {
                    deviceCode: urlParams.deviceCode,
                    storeCode: currentStoreCode,
                    type: urlParams.type
                },
                replace: true // 히스토리에서 현재 페이지를 대체
            });
            return; // useEffect 종료
        }
    }, [isCardAuthMode, navigate, urlParams.deviceCode, currentStoreCode, urlParams.type]);

    // 휴대폰 번호 입력란 자동 포커스
    useEffect(() => {
        devLog("Focus effect triggered, authMethod:", authMethod);
        if (authMethod === 'standard' && inputs.current[0]) {
            devLog("Attempting to focus input");
            inputs.current[0].focus();
        }
    }, [authMethod]);

    useEffect(() => {
        const isFirst = checkFirstTimeUser();
        setIsFirstTimeUser(isFirst);

        if (!isFirst) {
            const biometricSupportedInPast = localStorage.getItem('puffu_biometric_supported') === 'true';

            if (biometricSupportedInPast) {
                biometricService.hasRegisteredKey().then(hasKey => {
                    if (!hasKey) {
                        // 생체인증 초기화 감지됨 - 바로 standard 모드로 설정
                        setAuthMethod('standard');

                        toast.info(t("생체인증 정보가 초기화되었습니다. 로그인 후 다시 등록할 수 있습니다."), {
                            autoClose: 4000,
                            position: toast.POSITION.BOTTOM_CENTER
                        });
                    }
                }).catch(err => {
                    console.error("생체인증 키 확인 오류:", err);
                });
            }
        }
    }, [t]);

    // 생체인증 지원 여부만 확인
    useEffect(() => {
        const isSupported = biometricService.isSupported();
        setBiometricStatus(prev => ({
            ...prev,
            supported: isSupported,
            platform: biometricService.getPlatform()
        }));
    }, []);

    useEffect(() => {
        // 브라우저 뒤로가기 이벤트 처리
        const handleBackButton = (e) => {
            if (window.history.state && window.history.state.phoneNumber) {
                e.preventDefault();
                setPhoneNumber(window.history.state.phoneNumber);
                if (inputs.current[0]) {
                    inputs.current[0].setValue(window.history.state.phoneNumber);
                }
            }
        };

        window.addEventListener('popstate', handleBackButton);
        return () => {
            window.removeEventListener('popstate', handleBackButton);
        };
    }, []);

    useEffect(() => {
        // 생체인증 지원 여부 확인 (중복이지만 기존 코드 유지)
        const isSupported = biometricService.isSupported();
        setBiometricStatus(prev => ({
            ...prev,
            supported: isSupported,
            platform: biometricService.getPlatform()
        }));
    }, []);

    // ==================== useEffect 끝 ====================

    window.handleLocationFromIOS = (location) => {
        navigate(location);
    };

    const handleTermsCheckboxChange = () => {
        setTermsChecked(!termsChecked);
    };

    const handlePrivacyCheckboxChange = () => {
        setPrivacyChecked(!privacyChecked);
    };

    const handleBiometricLogin = async () => {
        if (!biometricStatus.supported) return;

        setBiometricStatus(prev => ({ ...prev, loading: true, error: null }));

        // 최대 시도 횟수와 현재 시도 횟수 추적
        let maxAttempts = 5;
        let currentAttempt = 0;
        let success = false;

        // 생체인증 시도 함수
        const attemptBiometricAuth = async () => {
            try {
                currentAttempt++;
                // 간단한 챌린지 데이터 생성 (타임스탬프만 포함)
                const challengeData = `login_challenge_${Date.now()}`;

                // 사용자 ID 없이 생체인증 시도 (null 전달)
                const result = await biometricService.authenticateAndSignData(null, challengeData);

                // 서버에 생체인증 검증 요청
                let verifyData = {
                    biometricSignature: result.signature,
                    biometricChallenge: result.data,
                    platform: biometricService.getPlatform()
                };
                devLog("서버 검증 결과:", verifyData.platform);

                // 생체인증 검증
                requester.verifyBiometricSignature(verifyData, (verifyResult) => {

                    if (verifyResult.code === 0) {
                        // 생체인증 검증 성공, 받은 사용자 정보로 로그인 진행
                        success = true;
                        const memberInfo = verifyResult.data;

                        // memberInfo가 null이거나 undefined인 경우 처리
                        if (!memberInfo) {
                            console.error("생체인증 검증 성공했으나 사용자 정보가 없습니다.");
                            toast.error(t("사용자 정보를 찾을 수 없습니다. 일반 로그인을 시도해주세요."), {
                                autoClose: 3000,
                                position: toast.POSITION.BOTTOM_CENTER
                            });
                            setBiometricStatus(prev => ({ ...prev, loading: false }));
                            setAuthMethod('standard');
                            return;
                        }

                        // 로그인 요청
                        let signInData = {
                            storeId: "root",
                            userName: memberInfo.userName || memberInfo.mobileNumber || "",
                            role: "MEMBER"
                        };

                        requester.userNumberSignIn(signInData, (signInResult) => {
                            if (signInResult.code === 0) {
                                // 로그인 성공
                                const tokenData = signInResult.data.token;
                                dispatch(AuthReducer.actions.setToken(tokenData));

                                // WebView에 토큰 전달
                                if (window.webkit?.messageHandlers?.BRIDGE) {
                                    window.webkit.messageHandlers.BRIDGE.postMessage({
                                        command: "setToken",
                                        token: tokenData
                                    });
                                }

                                // 메인 페이지로 이동
                                navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
                            } else {
                                // 로그인 실패
                                toast.error(t("로그인 중 오류가 발생했습니다. 일반 로그인을 시도해주세요."));
                                setAuthMethod('standard');
                            }
                        });
                    } else {
                        // 서버 검증 실패
                        if (biometricService.getPlatform() === 'ios') {
                            // iOS에서 검증 실패는 대부분 등록되지 않은 사용자이므로
                            // 바로 표준 로그인으로 전환
                            toast.info(t("생체인증이 등록되지 않았습니다. 일반 로그인을 이용해주세요."), {
                                autoClose: 3000,
                                position: toast.POSITION.BOTTOM_CENTER
                            });
                            setBiometricStatus(prev => ({ ...prev, loading: false }));
                            setAuthMethod('standard');
                            return;
                        }

                        if (currentAttempt < maxAttempts) {
                            // 잠시 대기 후 재시도
                            setTimeout(() => {
                                attemptBiometricAuth();
                            }, 1500);
                        } else {
                            // 최대 시도 횟수 초과
                            toast.error(t("생체인증 확인에 여러 번 실패했습니다. 일반 로그인을 시도해주세요."), {
                                autoClose: 3000,
                                position: toast.POSITION.BOTTOM_CENTER
                            });
                            setAuthMethod('standard');
                        }
                    }
                });
            } catch (err) {
                // 생체인증 자체 실패 (사용자 취소, 하드웨어 오류 등)
                if (currentAttempt < maxAttempts) {
                    // iOS 미등록 사용자 처리 추가
                    if (biometricService.getPlatform() === 'ios' &&
                        (err.message.includes('등록되지 않은') || err.message.includes('not registered') ||
                            err.message.includes('No matching key') || err.message === 'iOS 생체인증 실패')) {

                        // 등록되지 않은 사용자로 간주하고 바로 표준 로그인으로 전환
                        toast.info(t("생체인증이 등록되지 않았습니다. 일반 로그인을 이용해주세요."), {
                            autoClose: 3000,
                            position: toast.POSITION.BOTTOM_CENTER
                        });
                        setBiometricStatus(prev => ({ ...prev, loading: false }));
                        setAuthMethod('standard');
                        return;
                    }

                    setBiometricStatus(prev => ({
                        ...prev,
                        error: err.message
                    }));

                    // 잠시 대기 후 재시도
                    setTimeout(() => {
                        attemptBiometricAuth();
                    }, 1500);
                } else {
                    // 최대 시도 횟수 초과
                    toast.error(t("생체인증에 여러 번 실패했습니다. 일반 로그인을 시도해주세요."), {
                        autoClose: 3000,
                        position: toast.POSITION.BOTTOM_CENTER
                    });
                    setBiometricStatus(prev => ({
                        ...prev,
                        error: err.message
                    }));
                    setAuthMethod('standard');
                }
            }
        };

        // 첫 번째 시도 시작
        await attemptBiometricAuth();

        // 모든 시도가 끝났을 때 로딩 상태 해제
        if (!success) {
            setBiometricStatus(prev => ({ ...prev, loading: false }));
        }
    };

    // 생체인증 선택 시 바로 인증 시작
    const handleBiometricSelect = () => {
        setAuthMethod('biometric');
        handleBiometricLogin();
    };

    const onMemberAccessClick = () => {
        setTermsChecked(true);
        setPrivacyChecked(true);

        validateInputs(inputs.current).then((isValid) => {
            if (isValid.isValid) {
                const currentPhoneNumber = inputs.current[0].getValue();
                window.history.pushState(
                    { phoneNumber: currentPhoneNumber },
                    '',
                    window.location.pathname
                );

                if (currentPhoneNumber === "01088888888") {
                    let data = { storeId: "root", userName: currentPhoneNumber, role: "MEMBER" };
                    requester.userNumberSignIn(data, (result) => {
                        if (result.code === 0) {
                            requester.useYnConfirm(data, (useYn) => {
                                let isWithdrawn = useYn.data;
                                if (isWithdrawn) {
                                    toast.error(t("탈퇴된 회원입니다."), {
                                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                                    });
                                } else {
                                    let tokenData = result.data.token;
                                    dispatch(AuthReducer.actions.setToken(tokenData));

                                    setTimeout(() => {
                                        if (window.webkit?.messageHandlers?.BRIDGE) {
                                            window.webkit.messageHandlers.BRIDGE.postMessage({ command: "setToken", token: tokenData });
                                        }
                                    }, 500);

                                    const isAndroid = /Android/i.test(navigator.userAgent);
                                    navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
                                }
                            });
                        }
                    });
                } else {
                    navigate("/memberAccess", {
                        state: {
                            storeCode: currentStoreCode,
                            mobileNumber: currentPhoneNumber,
                            fromVerification: true,
                            ...(urlParams.deviceCode && { deviceCode: urlParams.deviceCode }),
                            ...(urlParams.type && { type: urlParams.type })
                        }
                    });
                }
            } else {
                toast.error(t("올바른 연락처를 입력해주세요"), {
                    autoClose: 1000, position: toast.POSITION.TOP_CENTER
                });
                inputs.current[0]?.focus();
            }
        });
    };

    const handlePhoneNumberChange = (value) => {
        setPhoneNumber(value);
    };

    // 카드 인증 모드일 때는 로딩 화면 렌더링 (깜빡임 방지)
    if (isOurAppWebView() && isCardAuthMode) {
        return (
            <Container>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh"
                }}>
                    <div style={{
                        width: "40px",
                        height: "40px",
                        border: "3px solid #f0f0f0",
                        borderTop: "3px solid var(--main-color2)",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                    }} />
                </div>
                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </Container>
        );
    }

    return (
        <Container>
            <div style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                minHeight: "100vh",
                padding: "40px 20px"
            }}>
                {/* 헤더 영역 */}
                <div style={{ marginBottom: "40px" }}>
                    <h1 style={{
                        color: "var(--main-color2)",
                        fontSize: "32px",
                        fontWeight: "700",
                        marginBottom: "12px"
                    }}>
                        PUFFU 로그인
                    </h1>
                    <p style={{
                        color: "#666666",
                        fontSize: "16px"
                    }}>
                        {!authMethod ? "로그인 방식을 선택해주세요" : "휴대폰 번호로 간편하게 로그인하세요"}
                    </p>
                </div>

                {!authMethod ? (
                    // 로그인 방식 선택 화면
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {/* {biometricStatus.supported && (
                            <button
                                onClick={handleBiometricSelect}
                                style={{
                                    width: "100%",
                                    height: "56px",
                                    backgroundColor: "var(--main-color2)",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: "#ffffff",
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    cursor: "pointer"
                                }}
                            >
                                생체인증으로 간편로그인
                            </button>
                        )} */}

                        <button
                            onClick={() => setAuthMethod('standard')}
                            style={{
                                width: "100%",
                                height: "56px",
                                backgroundColor: "var(--main-color2)",
                                border: "none",
                                borderRadius: "8px",
                                color: "#ffffff",
                                fontSize: "18px",
                                fontWeight: "700",
                                cursor: "pointer"
                            }}
                        >
                            인증 후 로그인&회원가입
                        </button>
                    </div>
                ) : authMethod === 'biometric' ? (
                    // 생체인증 진행 중 화면
                    <div style={{ textAlign: "center" }}>
                        <p style={{ marginBottom: "20px" }}>
                            생체인증을 진행중입니다...
                        </p>
                        <p style={{ marginBottom: "20px" }}>
                            등록하지 않은 사용자는 잠시후 화면이 이동합니다.
                        </p>
                        {biometricStatus.error && (
                            <p style={{ color: "red", marginTop: "10px" }}>
                                {biometricStatus.error}
                            </p>
                        )}
                    </div>
                ) : (
                    // 기존 휴대폰 번호 입력 화면
                    <>
                        {/* 입력 필드 영역 */}
                        <div style={{ marginBottom: "24px" }}>
                            <InputText
                                regExp={[mobileNoFormat]}
                                mobileText
                                type={"number"}
                                ref={el => (inputs.current[0] = el)}
                                placeHolder="휴대폰 번호 입력"
                                number
                                value={phoneNumber}
                                onChange={handlePhoneNumberChange}
                                style={{
                                    height: "56px",
                                    borderRadius: "8px",
                                    fontSize: "16px",
                                    backgroundColor: "#ffffff",
                                    padding: "0 16px",
                                    width: "100%",
                                    color: "#333333"
                                }}
                            />
                        </div>

                        {/* 약관 동의 영역 */}
                        <div style={{ marginBottom: "32px" }}>
                            <div className={styles.agreementArea}>
                                <div className={styles.checkboxWrapper}>
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        checked={termsChecked}
                                        onChange={handleTermsCheckboxChange}
                                        className={styles.checkbox}
                                    />
                                    <label
                                        htmlFor="terms"
                                        className={styles.checkmark1}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className={styles.agreementText}>
                                        이용약관에 동의합니다
                                    </span>
                                    <a href="#" className={styles.viewButton} onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        navigate(`/ws/${props.storeCode}/policy`, { state: { signIn: true } });
                                    }}>
                                        보기
                                    </a>
                                </div>
                            </div>
                            <div className={styles.agreementArea}>
                                <div className={styles.checkboxWrapper}><input
                                    type="checkbox"
                                    id="privacy"
                                    checked={privacyChecked}
                                    onChange={handlePrivacyCheckboxChange}
                                    className={styles.checkbox}
                                />
                                    <label
                                        htmlFor="privacy"
                                        className={styles.checkmark1}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className={styles.agreementText}>
                                        개인정보처리방침에 동의합니다
                                    </span>
                                    <a href="#" className={styles.viewButton} onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        navigate(`/ws/${props.storeCode}/privacy`, { state: { signIn: true } });
                                    }}>
                                        보기
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* 로그인 버튼 */}
                        <button
                            onClick={onMemberAccessClick}
                            style={{
                                width: "100%",
                                height: "56px",
                                backgroundColor: "var(--main-color2)",
                                border: "none",
                                borderRadius: "8px",
                                color: "#ffffff",
                                fontSize: "18px",
                                fontWeight: "700",
                                cursor: "pointer",
                                marginBottom: "16px"
                            }}
                        >
                            약관에 동의하고 로그인 & 회원가입
                        </button>

                        {/* 안내 문구 */}
                        <p style={{
                            color: "#999999",
                            fontSize: "14px",
                            textAlign: "center",
                            lineHeight: "1.5"
                        }}>
                            휴대폰 인증을 통해 안전하게 로그인할 수 있습니다.<br />
                            본인 명의의 휴대폰 번호를 입력해주세요.
                        </p>
                    </>
                )}
            </div>
        </Container>
    );
}

export default MobileNumberSignIn;